import React from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Layers, Plus, Trash2, GripVertical } from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableChildRow({ sub, idx, itemForm, setItemForm, icons, colors }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sub.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  const patch = (field, value) => setItemForm({
    ...itemForm,
    children: itemForm.children.map((c, i) => (i === idx ? { ...c, [field]: value } : c)),
  });
  return (
    <div ref={setNodeRef} style={style} className="rounded-md border border-slate-200 bg-white p-2 space-y-2" data-testid={`child-row-${idx}`}>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 shrink-0"
          data-testid={`child-drag-${idx}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <Input
          value={sub.label}
          onChange={(e) => patch('label', e.target.value)}
          placeholder="Libellé (ex : Professionnels)"
          className="text-sm h-8"
          data-testid={`child-label-${idx}`}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-red-500 shrink-0"
          data-testid={`child-remove-${idx}`}
          onClick={() => setItemForm({ ...itemForm, children: itemForm.children.filter((_, i) => i !== idx) })}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <Input
        value={sub.to}
        onChange={(e) => patch('to', e.target.value)}
        placeholder="URL ou route (ex : https://… ou /page)"
        className="font-mono text-xs h-8"
        data-testid={`child-to-${idx}`}
      />
      <div className="grid grid-cols-2 gap-2">
        <select
          value={sub.icon || 'Sparkles'}
          onChange={(e) => patch('icon', e.target.value)}
          className="w-full h-8 rounded-md border border-slate-300 px-2 text-xs bg-white"
          data-testid={`child-icon-${idx}`}
        >
          {icons.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
        </select>
        <select
          value={sub.color || 'blue'}
          onChange={(e) => patch('color', e.target.value)}
          className="w-full h-8 rounded-md border border-slate-300 px-2 text-xs bg-white"
          data-testid={`child-color-${idx}`}
        >
          {colors.map((cl) => <option key={cl} value={cl}>{cl}</option>)}
        </select>
      </div>
    </div>
  );
}

export const NavItemChildrenEditor = ({ itemForm, setItemForm, icons, colors }) => {
  const children = (itemForm.children || []).map((c, i) => (c.id ? c : { ...c, id: `c-${i}-${Date.now()}` }));
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = children.findIndex((c) => c.id === active.id);
    const newIndex = children.findIndex((c) => c.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    setItemForm({ ...itemForm, children: arrayMove(children, oldIndex, newIndex) });
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3" data-testid="item-form-children">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold flex items-center gap-1.5">
          <Layers className="h-3.5 w-3.5" /> Sous-entrées (menu à 2 niveaux)
        </Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 text-xs border-dashed"
          data-testid="add-child-btn"
          onClick={() => setItemForm({
            ...itemForm,
            children: [...children, { id: `c-${Date.now()}`, label: '', to: '', icon: 'Sparkles', color: 'blue' }],
          })}
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter
        </Button>
      </div>
      {children.length === 0 && (
        <p className="text-[11px] text-slate-400">Aucune sous-entrée. Glissez la poignée pour réordonner. Un lien externe (https://…) s'ouvre dans un nouvel onglet.</p>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={children.map((c) => c.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {children.map((sub, idx) => (
              <SortableChildRow
                key={sub.id}
                sub={sub}
                idx={idx}
                itemForm={{ ...itemForm, children }}
                setItemForm={setItemForm}
                icons={icons}
                colors={colors}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {children.length > 0 && (
        <div className="mt-2 rounded-md border border-dashed border-slate-300 bg-white p-2" data-testid="children-preview">
          <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5">Aperçu du sous-menu</p>
          <div className="rounded-lg bg-slate-900 p-2 space-y-0.5">
            <div className="px-2 py-1 text-xs font-semibold text-white flex items-center gap-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-pink-400" />
              {itemForm.label || 'Élément parent'}
            </div>
            <div className="ml-4 pl-2 border-l border-slate-700/50 space-y-0.5">
              {children.map((sub, i) => (
                <div key={sub.id} className="flex items-center gap-2 px-2 py-1 text-[11px] text-slate-300" data-testid={`children-preview-item-${i}`}>
                  <span className="text-slate-500 tabular-nums">{i + 1}.</span>
                  <span className="truncate">{sub.label || '(sans libellé)'}</span>
                  {/^https?:\/\//.test(sub.to || '') && (
                    <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 shrink-0">externe ↗</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
